import { createContext, useContext } from 'react';

import type { Release, ReleaseRef } from '../types/taxonomy-studio.types';

/**
 * Which version the studio is showing and whether it can be edited, shared by the
 * status bar, every page and every edit control. Built once in the layout
 * (`useTaxonomyStudioState`) from the `?release=` search param, the polled draft and
 * the release list.
 */
export interface TaxonomyStudioContextValue {
  /** The ref every read sends: `'draft'` (default), `'live'` or a release number. */
  releaseRef: ReleaseRef;
  /** Switches version; `undefined` returns to the default (draft-or-live). */
  setReleaseRef: (ref: ReleaseRef | undefined) => void;
  draft: Release | null;
  live: Release | null;
  releases: Release[];
  /** The release the view resolves to (draft, live or an earlier one), when known. */
  viewing: Release | null;
  /** True when the view is the head (draft, or live with no draft) and the user may write. */
  isEditable: boolean;
  /** Viewing an earlier (superseded) version — show the warning bar. */
  isPast: boolean;
  /** Viewing live while a draft is open — read-only, but not "earlier". */
  isLiveWhileDraft: boolean;
  canPublish: boolean;
  /** `draft_version` moves not made by this tab. */
  othersChanges: number;
  /** Accepts the others' changes as seen and reloads every taxonomy read. */
  acknowledgeOthers: () => void;
}

export const TaxonomyStudioContext = createContext<TaxonomyStudioContextValue | null>(null);

/** Reads the studio context; throws outside the `/taxonomy` layout. */
export function useTaxonomyStudio(): TaxonomyStudioContextValue {
  const ctx = useContext(TaxonomyStudioContext);
  if (!ctx) throw new Error('useTaxonomyStudio must be used inside the Taxonomy Studio layout');
  return ctx;
}
